<template>
  <div
    v-if="video || remoteVideo"
    class="grid-container"
    :class="[
      options.spacing === 'small' ? 'pt-6 lg:pt-10' : '',
      options.spacing === 'large' ? 'pt-12 lg:pt-20' : '',
      paragraphClassList,
    ]"
  >
    <div :class="[layoutClasses]">
      <MediaRemoteVideo
        v-if="remoteVideo"
        :video="remoteVideo"
        :is-landing="false"
        :image-style="imageStyle"
        :is-hero="false"
      />

      <MediaVideo
        v-else-if="video"
        v-bind="video"
        ref="mediaVideo"
        :video="video.video"
        class="flex items-center justify-center relative"
        controls
        :image-style="imageStyle"
        :is-landing="false"
        :autoplay="false"
        :is-hero="false"
        :poster="video.poster"
      />
      <Caption :caption="videoDescription" :copyright="source" class="mt-3" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  MediaRemoteVideoFragment,
  MediaVideoFragment,
  ParagraphVideoFragment,
} from '#graphql-operations'

const props = defineProps<{
  video?: ParagraphVideoFragment['video']
  videoDescription?: ParagraphVideoFragment['videoDescription']
  contextDescription?: ParagraphVideoFragment['contextDescription']
  source?: ParagraphVideoFragment['source']
}>()

const { options } = defineBlokkli({
  bundle: 'video',
  editor: {
    editTitle: (el) => el.querySelector('h2')?.innerText,
  },
  globalOptions: ['spacing'],
  options: {
    format: {
      type: 'radios',
      label: 'Bildformat',
      default: 'text',
      displayAs: 'icons',
      options: {
        big: { label: 'large', icon: 'icon-blokkli-option-large' },
        text: { label: 'small', icon: 'icon-blokkli-option-small' },
      },
    },
  },
})
const paragraphClassList = computed(() => {
  const classList = []

  if (options.value.spacing === 'small') {
    classList.push('py-6', 'lg:py-10')
  } else if (options.value.spacing === 'large') {
    classList.push('py-12', 'lg:py-20')
  }
  return classList
})

const bigGrid = defineImageStyle({
  type: 'sizes',
  aspectRatio: 16 / 9,
  sizes: {
    xs: 770,
    sm: 984,
    md: 1380,
    lg: 1380,
  },
})

const textGrid = defineImageStyle({
  type: 'sizes',
  aspectRatio: 16 / 9,
  sizes: {
    xs: 728,
    sm: 1000,
    md: 912,
    lg: 912,
  },
})

/**
 * The responsive image style name to use.
 */
const imageStyle = computed(() => {
  if (options.value.format === 'big') {
    return bigGrid
  }
  return textGrid
})

const video = computed(() => {
  if (props.video && 'video' in props.video) {
    return props.video as MediaVideoFragment
  }
  return null
})

const remoteVideo = computed(() => {
  if (props.video && 'url' in props.video) {
    return props.video as MediaRemoteVideoFragment
  }
  return null
})

const layoutClasses = computed(() => {
  if (options.value.format === 'big') {
    return 'col-span-4 sm:col-span-6 md:col-span-8 lg:col-span-12'
  }
  return 'col-span-4 sm:col-span-6 md:col-start-2 md:col-span-6 lg:col-start-3 lg:col-span-8'
})
</script>
